// Copyright (C) 2024-2025 Skylark Drones

export const DISPLAY_PLANS = process.env.VUE_APP_DISPLAY_PLANS === 'true'
export const QA_ENV = process.env.VUE_APP_ENV === 'qa'

export const faqList = [
  {
    key: 'plans',
    name: 'Plans',
    qna: [
      {
        question: 'Who is the Go Live plan meant for?',
        answer:
          '<p>Go Live is designed for <strong>inspection, surveillance, and ' +
          'field-operations teams</strong> who depend on real-time video visibility ' +
          'and coordination between pilots and remote viewers.</p>'
      },
      {
        question: 'Can I have multiple plans at once?',
        answer:
          '<p>No. Each workspace can have <strong>only one active plan</strong> ' +
          'at a time. You can scale your operations by purchasing <strong>Drone ' +
          'Packs</strong> for extra drones.</p>'
      },
      {
        question: 'Can I downgrade back to Free later?',
        answer:
          '<p>Yes. You can downgrade anytime. Your Go Live features remain active ' +
          'until the end of your billing cycle, after which your workspace ' +
          'reverts to Free.</p>'
      },
      {
        question: 'Do I lose my recordings if I downgrade?',
        answer:
          '<p>No. Your recordings stay accessible for <strong>180 days</strong> ' +
          'after your plan ends. You can download them anytime before that.</p>'
      }
    ]
  },
  {
    key: 'billing',
    name: 'Billing',
    qna: [
      {
        question: 'How does billing work?',
        answer:
          '<p>Go Live is billed <strong>per drone</strong>, starting from your ' +
          'date of purchase. You can choose <strong>monthly or yearly billing</strong> ' +
          'when you subscribe.</p>'
      },
      {
        question: 'What is the difference between monthly and yearly billing?',
        answer:
          '<ul>' +
          '<li><strong>Monthly:</strong> Pay every 30 days from your purchase date.</li>' +
          '<li><strong>Yearly:</strong> Pay once for 12 months upfront and enjoy a ' +
          'discounted rate.</li>' +
          '</ul>' +
          '<p>Your drones remain paired for the full duration of your chosen cycle.</p>'
      },
      {
        question: 'When does my billing renew?',
        answer:
          '<p>Your billing cycle renews automatically after 30 days (monthly) or ' +
          '365 days (yearly) from the purchase date.</p>'
      },
      {
        question: 'Can I switch between monthly and yearly later?',
        answer:
          '<p>Yes. You can switch plans anytime. The change takes effect from your ' +
          'next renewal date.</p>'
      },
      {
        question: 'Can I cancel anytime?',
        answer:
          '<p>Yes. You can cancel anytime from your Subscriptions page. Your plan ' +
          'stays active until the end of your current billing cycle.</p>'
      },
      {
        question: 'Do I get a refund if I cancel early?',
        answer:
          '<p>No. All plans are prepaid. Cancellations take effect at the end of ' +
          'your billing period.</p>'
      },
      {
        question: 'What happens if payment fails?',
        answer:
          '<p>We will retry the payment automatically and notify you by email. If ' +
          'it still fails, your Go Live access will pause until payment succeeds.</p>'
      },
      {
        question: 'Do I receive invoices or receipts?',
        answer:
          '<p>Yes. You will receive invoices automatically after each successful ' +
          'payment, and you can download them anytime from your Billing section.</p>'
      }
    ]
  },
  {
    key: 'pairing',
    name: 'Drone Pairing',
    qna: [
      {
        question: 'How do I pair a drone with my Go Live plan?',
        answer:
          '<p>You can pair your drone from either platform:</p>' +
          '<div style="margin-top:-8px;">' +
          '<ul>' +
          '<li><strong>DMO Web Portal:</strong> Go to <strong>Settings → Billing → ' +
          'Paired Drones</strong></li>' +
          '<li><strong>DMO Android:</strong> Go to <strong>Settings → ' +
          'Paired Drones</strong></li>' +
          '</ul>' +
          '</div>' +
          '<p style="margin-top:16px;">Once you’re in the pairing section, you can ' +
          'choose from three methods:</p>' +
          '<div style="margin-top:-8px;">' +
          '<ol>' +
          '<li><strong>Auto-detect (DMO Android only):</strong> Connect your drone to ' +
          'DMO, and it will automatically detect and pair it.</li>' +
          '<li><strong>Recently used drones:</strong> Select a drone from your ' +
          'recently connected list for quick pairing.</li>' +
          '<li><strong>Manual serial entry:</strong> Enter the drone’s serial number ' +
          'manually (available only if it has been connected to DMO before).</li>' +
          '</ol>'
      },
      {
        question: 'Can I pair multiple drones?',
        answer:
          '<p>Yes. Your plan includes a base number of pairing slots. You can ' +
          'increase this limit by purchasing <strong>Drone Packs</strong>.</p>'
      },
      {
        question: 'My drone crashed or is under repair. Can I move my pairing?',
        answer:
          '<p>Yes. Contact support with your workspace ID and drone serial, and we ' +
          'will help transfer your pairing so you can continue operations without ' +
          'downtime.</p>'
      },
      {
        question: 'Can I pair a rented or borrowed drone?',
        answer:
          '<p>Yes, as long as the drone belongs to your workspace. Pair it normally ' +
          'and unpair it when the project ends. Unpairing is allowed only after the ' +
          'billing cycle ends, except during the 30-minute correction window.</p>'
      },
      {
        question: 'We rotate drones between pilots. Will Go Live still work?',
        answer:
          '<p>Yes. Pairings are linked to the <strong>drone</strong>, not the pilot. ' +
          'Any pilot in your workspace can use Go Live features on that paired drone. ' +
          'External pilots will need their own workspace and plan.</p>'
      },
      {
        question: 'I lost access to my drone. What happens now?',
        answer:
          '<p>If your drone was sold, lost, or replaced, contact support. We will ' +
          'verify ownership and transfer your pairing at the end of your billing ' +
          'cycle.</p>'
      },
      {
        question: 'Do I need to re-pair drones after renewal?',
        answer:
          '<p>No. Your existing pairings remain active automatically after each ' +
          'renewal.</p>'
      },
      {
        question: 'Can I unpair a drone anytime?',
        answer:
          '<p>No. To prevent misuse, drones can only be unpaired <strong>after the ' +
          'billing cycle ends</strong>, except within the 30-minute grace window or ' +
          'verified emergencies such as crashes.</p>'
      },
      {
        question:
          'My drone serial number is not being accepted. What should I do?',
        answer:
          '<p>Ensure the serial number matches exactly what is shown in your DJI ' +
          'Fly or Pilot 2 application. If it still fails, send us a screenshot and ' +
          'we will verify it for you.</p>'
      }
    ]
  },
  {
    key: 'packs',
    name: 'Packs',
    qna: [
      {
        question: 'What are Drone Packs?',
        answer:
          '<p>Drone Packs allow you to add more drones to your Go Live plan. At ' +
          'launch, we offer two options — a <strong>1-Drone Pack</strong> and ' +
          'a <strong>3-Drone Pack</strong>.</p>'
      },
      {
        question: 'How do Drone Packs work?',
        answer:
          '<p>Each pack adds more pairing slots to your Go Live plan.</p>' +
          '<p><em>Example:</em> If your plan supports one drone and you buy a ' +
          '3-Drone Pack, you can pair up to four drones in total.</p>'
      },
      {
        question: 'How are Drone Packs billed?',
        answer:
          '<p>Packs follow your main plan’s billing type — monthly or yearly. If ' +
          'you buy a pack mid-cycle, you will only be charged for the remaining ' +
          'days of that cycle.</p>'
      },
      {
        question: 'Can I remove a Drone Pack anytime?',
        answer:
          '<p>Yes. You can remove it anytime, but it remains active until the end ' +
          'of your billing cycle.</p>'
      },
      {
        question: 'Do Drone Packs renew automatically?',
        answer:
          '<p>Yes. Drone Packs renew along with your main Go Live plan each cycle ' +
          'unless you cancel them.</p>'
      },
      {
        question: 'Can I use Drone Packs across different workspaces?',
        answer:
          '<p>No. Packs are tied to the workspace where they were purchased. Each ' +
          'workspace requires its own packs.</p>'
      },
      {
        question: 'Can I combine multiple Packs?',
        answer:
          '<p>Yes. You can stack multiple Drone Packs — for example, a 1-Pack and ' +
          'a 3-Pack — to increase your total pairing limit.</p>'
      }
    ]
  }
]

export const customPlan = {
  message: 'Unlock Pro Features for 1 Linked Drone',
  secondary_message: 'Tailored for scale, control, and security',
  summary: [
    'Custom workflows and feature sets',
    'Scalable fleet and user management',
    'On-premise or private cloud deployment',
    'Centralized data and mission control',
    'Dedicated success and support team'
  ],
  id: 'custom_plan',
  key: 'custom_plan',
  name: 'Enterprise',
  prices: [],
  period: 'monthly',
  features: []
}

export const currencySymbol = {
  USD: '$',
  INR: '₹',
  GYD: 'GY$'
}

export const plansKey = {
  noLimit: 'no_limit',
  free: 'free'
}

export const planRecurrence = {
  monthly: 'monthly',
  none: 'none'
}

export const featureLimitType = {
  general: 'general',
  custom: 'custom'
}

export const planPeriod = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
}

export const environmentPlanPeriods = QA_ENV
  ? [planPeriod.DAILY, planPeriod.WEEKLY]
  : [planPeriod.MONTHLY, planPeriod.YEARLY]

export const defaultPlanPeriod = environmentPlanPeriods[0]
export const secondaryPlanPeriod = environmentPlanPeriods[1]

export const planType = {
  GENERAL: 'general',
  CUSTOM: 'custom'
}

export const planPeriodLabel = {
  [planPeriod.DAILY]: 'day',
  [planPeriod.WEEKLY]: 'week',
  [planPeriod.MONTHLY]: 'month',
  [planPeriod.YEARLY]: 'year'
}

export const planPeriodShortLabel = {
  [planPeriod.DAILY]: 'day',
  [planPeriod.WEEKLY]: 'week',
  [planPeriod.MONTHLY]: 'mo',
  [planPeriod.YEARLY]: 'yr'
}
